/**
 * Persists in-progress flight recordings so a crash or WebView kill does not lose GPS data.
 *
 * Storage layout (inside Directory.Data):
 *   flights/draft.json              — RecordingDraftMeta
 *   flights/drafts/recording.csv    — append-only GPS track
 */

import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import type { FlightPoint } from '@/types/flight';
import { CSV_HEADER, csvToPoints, pointToCsvRow } from '@/lib/trackCsv';

const DRAFT_META_PATH = 'flights/draft.json';
const DRAFT_TRACK_PATH = 'flights/drafts/recording.csv';

export interface RecordingDraftMeta {
  id: string;
  startTime: number;
  pointCount: number;
  lastCheckpointAt: number;
}

export interface RecordingDraft {
  meta: RecordingDraftMeta;
  points: FlightPoint[];
}

async function writeDraftMeta(meta: RecordingDraftMeta): Promise<void> {
  await Filesystem.writeFile({
    path: DRAFT_META_PATH,
    data: JSON.stringify(meta, null, 2),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  });
}

/** Create a new draft track file with CSV header only. */
export async function createRecordingDraft(id: string, startTime: number): Promise<void> {
  await Filesystem.writeFile({
    path: DRAFT_TRACK_PATH,
    data: `${CSV_HEADER}\n`,
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  });
  await writeDraftMeta({
    id,
    startTime,
    pointCount: 0,
    lastCheckpointAt: Date.now(),
  });
}

/** Append new GPS points to the draft CSV and update metadata. */
export async function appendRecordingDraftPoints(
  meta: RecordingDraftMeta,
  points: FlightPoint[],
): Promise<RecordingDraftMeta> {
  if (points.length === 0) return meta;

  const rows = points.map(pointToCsvRow).join('\n');
  await Filesystem.appendFile({
    path: DRAFT_TRACK_PATH,
    data: `${rows}\n`,
    directory: Directory.Data,
    encoding: Encoding.UTF8,
  });

  const updated: RecordingDraftMeta = {
    ...meta,
    pointCount: meta.pointCount + points.length,
    lastCheckpointAt: Date.now(),
  };
  await writeDraftMeta(updated);
  return updated;
}

/** Load draft metadata and full track from disk. Returns null if no draft exists. */
export async function loadRecordingDraft(): Promise<RecordingDraft | null> {
  let metaRaw: string;
  try {
    const result = await Filesystem.readFile({
      path: DRAFT_META_PATH,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    metaRaw = result.data as string;
  } catch {
    return null;
  }

  let meta: RecordingDraftMeta;
  try {
    meta = JSON.parse(metaRaw) as RecordingDraftMeta;
  } catch {
    await clearRecordingDraft();
    return null;
  }

  if (!meta.id || !meta.startTime) {
    await clearRecordingDraft();
    return null;
  }

  let csv: string;
  try {
    const trackResult = await Filesystem.readFile({
      path: DRAFT_TRACK_PATH,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    csv = trackResult.data as string;
  } catch {
    await clearRecordingDraft();
    return null;
  }

  const points = csvToPoints(csv);
  return { meta: { ...meta, pointCount: points.length }, points };
}

/** Remove draft files after a recording is saved or discarded. */
export async function clearRecordingDraft(): Promise<void> {
  for (const path of [DRAFT_META_PATH, DRAFT_TRACK_PATH]) {
    try {
      await Filesystem.deleteFile({
        path,
        directory: Directory.Data,
      });
    } catch {
      // File may not exist
    }
  }
}
